import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { CertificateType } from '../../../../shared/models/certificate-type.enum';
import { OriginVhostAlias } from '../../models/origin-vhost.models';

@Component({
  selector: 'app-install-ssl-certificate-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatCheckboxModule,
  ],
  templateUrl: './install-ssl-certificate-dialog.component.html',
  styleUrl: './install-ssl-certificate-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallSslCertificateDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<InstallSslCertificateDialogComponent>);

  readonly data = inject<DialogData & { websiteId: number; aliases: OriginVhostAlias[] }>(
    MAT_DIALOG_DATA,
  );

  private fb = inject(FormBuilder);

  aliasOptions = this.data.aliases.map((alias) => ({ label: alias.alias, value: alias.alias }));

  certificateTypeOptions: { label: string; value: string }[] = [
    {
      value: CertificateType.LetsEncrypt,
      label: 'LetsEncrypt',
    },
    {
      value: CertificateType.SelfSigned,
      label: 'Self-Signed',
    },
  ];

  form = this.fb.nonNullable.group({
    type: [CertificateType.LetsEncrypt, [Validators.required]],
    websiteId: [0],

    // Let's encrypt field
    subDomains: [['']],
    // email: ['', [Validators.email]],
    vhostAliases: [true], // Utility control
    autoRenew: [true],

    // Self-signed field
    altNames: [{ value: this.aliasOptions.map((a) => a.label), disabled: true }],
  });

  get typeHint(): string {
    switch (this.form.controls.type.value) {
      case CertificateType.LetsEncrypt:
        return 'Let’s Encrypt is a free, automated, open certificate authority used to generate SSL certificates to be used by VirtualMax.';
      case CertificateType.SelfSigned:
        return 'A self-signed certificate is a locally generated SSL certificate for VMAX. It provides encrypted connections but is not trusted by browsers by default and may trigger security warnings.';
      default:
        return '';
    }
  }

  ngOnInit(): void {
    this.form.controls.websiteId.setValue(this.data.websiteId);
    this.form.controls.vhostAliases.valueChanges.subscribe((selectAll) => {
      if (selectAll) {
        this.form.controls.altNames.setValue(this.aliasOptions.map((a) => a.label));
        this.form.controls.altNames.disable();
      } else {
        this.form.controls.altNames.enable();
      }
      this.form.controls.altNames.updateValueAndValidity();
    });
  }

  submit(): void {
    if (this.form.invalid) return;
    const value = this.form.getRawValue();

    if (value.type === CertificateType.LetsEncrypt) {
      this.form.controls.subDomains.setValue(value.altNames);
      this.form.controls.altNames.reset();
    }

    const filtered = Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(this.form.getRawValue()).filter(([_, value]) => {
        if (value == null) return false;
        if (typeof value === 'string') return value.trim() !== '';
        if (Array.isArray(value)) return value.length > 0;
        return true;
      }),
    );
    this.dialogRef.close(filtered);
  }

  close(): void {
    this.dialogRef.close();
  }
}
